"use strict";
/*

https://github.com/mapbox/wellknown

Copyright (c) 2016, Mapbox

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.

*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.parse = parse;
exports.stringify = stringify;
const numberRegexp = /[-+]?([0-9]*\.[0-9]+|[0-9]+)([eE][-+]?[0-9]+)?/;
// Matches sequences like '100 100' or '100 100 100'.
const tuples = new RegExp('^' + numberRegexp.source + '(\\s' + numberRegexp.source + '){1,3}');
/*
 * Parse WKT and return GeoJSON.
 *
 * @param {string} _ A WKT geometry
 * @return {?Object} A GeoJSON geometry object
 */
function parse(input) {
    let parts = input.split(';');
    let _ = parts.pop();
    let srid = (parts.shift() || '').split('=').pop() || '';
    let i = 0;
    function $(re) {
        let match = _.substring(i).match(re);
        if (!match)
            return null;
        else {
            i += match[0].length;
            return match[0];
        }
    }
    function crs(obj) {
        if (obj && srid.match(/\d+/)) {
            obj.crs = {
                type: 'name',
                properties: {
                    name: 'urn:ogc:def:crs:EPSG::' + srid
                }
            };
        }
        return obj;
    }
    function white() { $(/^\s*/); }
    function multicoords() {
        white();
        let depth = 0;
        let rings = [];
        let stack = [rings];
        let pointer = rings;
        let elem;
        while (elem =
            $(/^(\()/) ||
                $(/^(\))/) ||
                $(/^(,)/) ||
                $(tuples)) {
            if (elem === '(') {
                stack.push(pointer);
                pointer = [];
                stack[stack.length - 1].push(pointer);
                depth++;
            }
            else if (elem === ')') {
                // For the case: Polygon(), ...
                if (pointer.length === 0)
                    return null;
                pointer = stack.pop() || [];
                // the stack was empty, input was malformed
                if (!pointer)
                    return null;
                depth--;
                if (depth === 0)
                    break;
            }
            else if (elem === ',') {
                pointer = [];
                stack[stack.length - 1].push(pointer);
            }
            else if (!elem.split(/\s/g).some(s => isNaN(s))) {
                // Split into values and only take first 3 if there are 4 (ZM coordinates)
                const values = elem.split(/\s/g).map(parseFloat);
                Array.prototype.push.apply(pointer, values.length === 4 ? values.slice(0, 3) : values);
            }
            else {
                return null;
            }
            white();
        }
        if (depth !== 0)
            return null;
        return rings;
    }
    function coords() {
        let list = [];
        let item;
        let pt;
        while (pt =
            $(tuples) ||
                $(/^(,)/)) {
            if (pt === ',') {
                list.push(item);
                item = [];
            }
            else if (!pt.split(/\s/g).some(s => isNaN(s))) {
                if (!item)
                    item = [];
                const values = pt.split(/\s/g).map(parseFloat);
                Array.prototype.push.apply(item, values.length === 4 ? values.slice(0, 3) : values);
            }
            white();
        }
        if (item)
            list.push(item);
        else
            return null;
        return list.length ? list : null;
    }
    function point() {
        if (!$(/^(point(\s+zm|\s+z)?)/i))
            return null;
        white();
        if (!$(/^(\()/))
            return null;
        let c = coords();
        if (!c)
            return null;
        white();
        if (!$(/^(\))/))
            return null;
        return {
            type: 'Point',
            coordinates: c[0]
        };
    }
    function multipoint() {
        if (!$(/^(multipoint(\s+zm|\s+z)?)/i))
            return null;
        white();
        let newCoordsFormat = _
            .substring(_.indexOf('(') + 1, _.length - 1)
            .replace(/\(/g, '')
            .replace(/\)/g, '');
        _ = 'MULTIPOINT (' + newCoordsFormat + ')';
        let c = multicoords();
        if (!c)
            return null;
        white();
        return {
            type: 'MultiPoint',
            coordinates: c
        };
    }
    function multilinestring() {
        if (!$(/^(multilinestring(\s+zm|\s+z)?)/i))
            return null;
        white();
        let c = multicoords();
        if (!c)
            return null;
        white();
        return {
            type: 'MultiLineString',
            coordinates: c
        };
    }
    function linestring() {
        if (!$(/^(linestring(\s+zm|\s+z)?)/i))
            return null;
        white();
        if (!$(/^(\()/))
            return null;
        let c = coords();
        if (!c)
            return null;
        if (!$(/^(\))/))
            return null;
        return {
            type: 'LineString',
            coordinates: c
        };
    }
    function polygon() {
        if (!$(/^(polygon(\s+zm|\s+z)?)/i))
            return null;
        white();
        let c = multicoords();
        if (!c)
            return null;
        return {
            type: 'Polygon',
            coordinates: c
        };
    }
    function multipolygon() {
        if (!$(/^(multipolygon(\s+zm|\s+z)?)/i))
            return null;
        white();
        let c = multicoords();
        if (!c)
            return null;
        return {
            type: 'MultiPolygon',
            coordinates: c
        };
    }
    function geometrycollection() {
        let geometries = [];
        let geometry;
        if (!$(/^(geometrycollection)/i))
            return null;
        white();
        if (!$(/^(\()/))
            return null;
        while (geometry = root()) {
            geometries.push(geometry);
            white();
            $(/^(,)/);
            white();
        }
        if (!$(/^(\))/))
            return null;
        return {
            type: 'GeometryCollection',
            geometries: geometries
        };
    }
    function root() {
        return point() ||
            linestring() ||
            polygon() ||
            multipoint() ||
            multilinestring() ||
            multipolygon() ||
            geometrycollection();
    }
    return crs(root());
}
/**
 * Stringifies a GeoJSON object into WKT
 */
function stringify(gj) {
    if (gj.type === 'Feature') {
        gj = gj.geometry;
    }
    function pairWKT(c) {
        return c.join(' ');
    }
    function ringWKT(r) {
        return r.map(pairWKT).join(', ');
    }
    function ringsWKT(r) {
        return r.map(ringWKT).map(wrapParens).join(', ');
    }
    function multiRingsWKT(r) {
        return r.map(ringsWKT).map(wrapParens).join(', ');
    }
    function wrapParens(s) { return '(' + s + ')'; }
    switch (gj.type) {
        case 'Point':
            return 'POINT (' + pairWKT(gj.coordinates) + ')';
        case 'LineString':
            return 'LINESTRING (' + ringWKT(gj.coordinates) + ')';
        case 'Polygon':
            return 'POLYGON (' + ringsWKT(gj.coordinates) + ')';
        case 'MultiPoint':
            return 'MULTIPOINT (' + ringWKT(gj.coordinates) + ')';
        case 'MultiPolygon':
            return 'MULTIPOLYGON (' + multiRingsWKT(gj.coordinates) + ')';
        case 'MultiLineString':
            return 'MULTILINESTRING (' + ringsWKT(gj.coordinates) + ')';
        case 'GeometryCollection':
            return 'GEOMETRYCOLLECTION (' + gj.geometries.map(stringify).join(', ') + ')';
        default:
            throw new Error('stringify requires a valid GeoJSON Feature or geometry object as input');
    }
}
